import React, { useState, useCallback, useMemo } from 'react';
import { Checkbox, Icon } from '@ann/web-components';
import {
  CardContainer,
  CheckboxWrapper,
  IconCircle,
  ContentArea,
  HeaderRow,
  TitleArea,
  Title,
  Badge,
  RightArea,
  UnreadDot,
  Timestamp,
  MenuButton,
  AISummaryRow,
  AISummaryBadge,
  AISummaryText,
  ExpandButton,
  QuoteSection,
  QuoteAvatar,
  QuoteContent,
  QuoteAuthor,
  QuoteText,
  FooterRow,
  AgentInfo,
  AgentAvatar,
  AgentName,
  MetaItem,
  Separator,
  LiveBadge,
  MoreBadge,
} from './CommunicationCard.styled';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Channel type icon config
const CHANNEL_ICONS = {
  EMAIL: { icon: 'email', bg: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)' },
  SMS: { icon: 'sms', bg: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' },
  CHAT: { icon: 'forum', bg: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)' },
  PHONE: { icon: 'phone', bg: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)' },
};

// Badge styles
const BADGE_STYLES = {
  LIVE: { bg: '#DCFCE7', color: '#16A34A' },
  HANDOFF: { bg: '#DBEAFE', color: '#2563EB' },
  VIP: { bg: '#F3E8FF', color: '#9333EA' },
  MISSED: { bg: '#FEE2E2', color: '#DC2626' },
  CALLBACK: { bg: '#FFEDD5', color: '#EA580C' },
  CLOSED: { bg: '#F3F4F6', color: '#6B7280' },
};

const CommunicationCard = ({
  communication,
  isSelected = false,
  onSelect,
  onClick,
  onMenuClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const {
    id,
    title,
    channelType,
    status,
    tags = [],
    isUnread,
    aiSummary,
    description,
    lastMessage,
    agent,
    owner,
    messageCount,
    duration,
    isLive,
    createdAt,
    lastUpdated,
    lastUpdate,
    startedAt,
  } = communication;

  const toggleExpand = useCallback((e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  }, []);

  const handleCheckboxChange = useCallback(() => {
    onSelect?.(id);
  }, [id, onSelect]);

  const handleCardClick = useCallback(() => {
    onClick?.(communication);
  }, [communication, onClick]);

  const handleMenuClick = useCallback((e) => {
    e.stopPropagation();
    onMenuClick?.(communication, e);
  }, [communication, onMenuClick]);

  // Get channel icon config
  const channelConfig = useMemo(() => {
    const type = (channelType || 'EMAIL').toUpperCase();
    return CHANNEL_ICONS[type] || CHANNEL_ICONS.EMAIL;
  }, [channelType]);

  // Get all badges to display
  const badges = useMemo(() => {
    const result = [];
    
    // Add status badge
    if (status && status !== 'ACTIVE') {
      const normalized = status.toUpperCase();
      if (BADGE_STYLES[normalized]) {
        result.push({ key: `status-${normalized}`, label: normalized === 'LIVE' ? 'Live' : status, ...BADGE_STYLES[normalized] });
      }
    }
    
    // Check if live
    if (isLive) {
      result.push({ key: 'live', label: 'Live', ...BADGE_STYLES.LIVE });
    }
    
    // Add tags
    tags.forEach((tag, idx) => {
      const normalized = tag.toUpperCase();
      const style = BADGE_STYLES[normalized] || { bg: '#E5E7EB', color: '#374151' };
      result.push({ key: `tag-${idx}`, label: tag, ...style });
    });
    
    return result;
  }, [status, isLive, tags]);

  // Format timestamp
  const formattedTime = useMemo(() => {
    const date = lastUpdated || lastUpdate || createdAt || startedAt;
    if (!date) return '';
    const d = dayjs(date);
    const now = dayjs();
    
    if (now.diff(d, 'hour') < 24) {
      return d.fromNow();
    } else if (now.diff(d, 'day') < 7) {
      return `${now.diff(d, 'day')} day${now.diff(d, 'day') > 1 ? 's' : ''} ago`;
    }
    return d.format('MM/DD/YYYY');
  }, [lastUpdated, lastUpdate, createdAt, startedAt]);

  // Get summary text
  const summaryText = aiSummary || description || 'No summary available.';

  // Agent or owner info
  const agentData = agent || owner;

  return (
    <CardContainer $selected={isSelected} onClick={handleCardClick}>
      {/* Checkbox */}
      <CheckboxWrapper onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
      </CheckboxWrapper>

      {/* Channel Icon */}
      <IconCircle $bgColor={channelConfig.bg}>
        <Icon name={channelConfig.icon} width="20px" height="20px" />
      </IconCircle>

      {/* Main Content */}
      <ContentArea>
        {/* Header Row */}
        <HeaderRow>
          <TitleArea>
            <Title>{title || 'Untitled'}</Title>
            {badges.map((badge) => (
              <Badge key={badge.key} $bgColor={badge.bg} $color={badge.color}>
                {badge.label}
              </Badge>
            ))}
          </TitleArea>
          <RightArea>
            {isUnread && <UnreadDot />}
            <Timestamp>{formattedTime}</Timestamp>
            <MenuButton onClick={handleMenuClick}>
              <Icon name="moreVert" width="18px" height="18px" />
            </MenuButton>
          </RightArea>
        </HeaderRow>

        {/* AI Summary Row */}
        <AISummaryRow>
          <AISummaryBadge>
            <Icon name="autoAwesome" width="12px" height="12px" />
            AI Summary
          </AISummaryBadge>
          <AISummaryText>{summaryText}</AISummaryText>
          <ExpandButton $expanded={isExpanded} onClick={toggleExpand}>
            <Icon name="expandMore" width="16px" height="16px" />
          </ExpandButton>
        </AISummaryRow>

        {/* Quote Section (collapsible) */}
        <QuoteSection $visible={isExpanded}>
          <QuoteAvatar>
            {lastMessage?.avatar ? (
              <img src={lastMessage.avatar} alt={lastMessage.author} />
            ) : (
              <Icon name="person" width="16px" height="16px" />
            )}
          </QuoteAvatar>
          <QuoteContent>
            <QuoteAuthor>{lastMessage?.author || 'Customer'}</QuoteAuthor>
            <QuoteText>{lastMessage?.text || description || 'No message content.'}</QuoteText>
          </QuoteContent>
        </QuoteSection>

        {/* Footer Row */}
        <FooterRow>
          {agentData && (
            <AgentInfo>
              <AgentAvatar>
                {agentData.avatar ? (
                  <img src={agentData.avatar} alt={agentData.name} />
                ) : (
                  <Icon name="person" width="14px" height="14px" />
                )}
              </AgentAvatar>
              <AgentName>{agentData.name}</AgentName>
            </AgentInfo>
          )}

          {messageCount > 0 && (
            <>
              <Separator>·</Separator>
              <MetaItem>
                <Icon name="chatBubbleOutline" width="14px" height="14px" />
                {messageCount}
              </MetaItem>
            </>
          )}

          {(duration || formattedTime) && (
            <>
              <Separator>·</Separator>
              <MetaItem>
                <Icon name="schedule" width="14px" height="14px" />
                {duration || formattedTime}
              </MetaItem>
            </>
          )}

          <Separator>·</Separator>
          <MetaItem>
            <Icon name="moodOutlined" width="14px" height="14px" />
          </MetaItem>

          {isLive && (
            <>
              <Separator>·</Separator>
              <LiveBadge>
                <Icon name="wifiTethering" width="12px" height="12px" />
                Live
              </LiveBadge>
            </>
          )}

          {communication.additionalCount > 0 && (
            <>
              <Separator>·</Separator>
              <MoreBadge>+{communication.additionalCount}</MoreBadge>
            </>
          )}
        </FooterRow>
      </ContentArea>
    </CardContainer>
  );
};

export default CommunicationCard;
